import { createContext, useContext, useState, ReactNode } from "react";
import { CreateClientRequest } from "../services/crefisaService/createClient";
import { OfertaPreAprovada } from "../services/crefisaService/getOperations";

type SimulationData = {
  clientId: string;
  isRefinancing: boolean;
  borrowedValue: number;
  preApproveOffers?: OfertaPreAprovada[];
};

type ProposalData = {
  value: number;
  prazo: number;
  simulationId?: string;
  refinancingId?: string;
  offerId?: string;
  ddd?: number;
  phone?: number;
  contract?: string;
};

interface CustomerData extends CreateClientRequest {
  customerId: string;
  id?: string;
}

interface CrefisaContextType {
  customerData: CustomerData | null;
  setCustomer: (customerData: Partial<CustomerData>) => void;
  clearCustomerData: () => void;
  proposalData: ProposalData | null;
  setProposal: (proposal: Partial<ProposalData>) => void;
  clearProposalData: () => void;
  simulationData: SimulationData | null;
  setSimulation: (simulation: Partial<SimulationData>) => void;
  clearSimulationData: () => void;
}

const CrefisaContext = createContext<CrefisaContextType | undefined>(undefined);

export function CrefisaProvider({ children }: { children: ReactNode }) {
  const [customerData, setCustomerData] = useState<CustomerData | null>(null);
  const [simulationData, setSimulationData] = useState<SimulationData | null>(
    null,
  );
  const [proposalData, setProposalData] = useState<ProposalData | null>(null);

  const setCustomer = (data: Partial<CustomerData>) => {
    setCustomerData((prev) => ({ ...prev, ...data }) as CustomerData | null);
  };
  const clearCustomerData = () => setCustomerData(null);

  const setProposal = (data: Partial<ProposalData>) => {
    setProposalData((prev) => ({ ...prev, ...data }) as ProposalData | null);
  };
  const clearProposalData = () => setProposalData(null);

  const setSimulation = (data: Partial<SimulationData>) => {
    setSimulationData(
      (prev) => ({ ...prev, ...data }) as SimulationData | null,
    );
  };
  const clearSimulationData = () => setSimulationData(null);

  return (
    <CrefisaContext.Provider
      value={{
        customerData,
        setCustomer,
        clearCustomerData,
        proposalData,
        setProposal,
        clearProposalData,
        simulationData,
        setSimulation,
        clearSimulationData,
      }}
    >
      {children}
    </CrefisaContext.Provider>
  );
}

export function useCrefisaContext() {
  const context = useContext(CrefisaContext);
  if (!context) {
    throw new Error("useCrefisaContext must be used within a CrefisaProvider");
  }
  return context;
}
